import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from './firebase';

export default function SubmissionPage() {
  const [formData, setFormData] = useState({
    artistName: '',
    submissionType: 'suspected_ai',
    musicLink: '',
    evidence: '',
    additionalInfo: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.artistName.trim()) {
      alert('Please enter an artist name');
      return;
    }

    if (!formData.musicLink.trim()) {
      alert('Please provide a music platform link (Spotify, Deezer, Apple Music, etc.)');
      return;
    }

    // Basic URL validation
    if (!formData.musicLink.includes('http')) {
      alert('Please enter a valid URL starting with http:// or https://');
      return;
    }

    setSubmitting(true);

    try {
      await addDoc(collection(db, 'submissions'), {
        artistName: formData.artistName.trim(),
        submissionType: formData.submissionType,
        musicLink: formData.musicLink.trim(),
        evidence: formData.evidence.trim(),
        additionalInfo: formData.additionalInfo.trim(),
        timestamp: new Date().toISOString(),
        status: 'pending'
      });

      setSubmitted(true);
      setFormData({
        artistName: '',
        submissionType: 'suspected_ai',
        musicLink: '',
        evidence: '',
        additionalInfo: ''
      });

      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      console.error('Submission error:', error);
      alert('Error submitting. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
    <a href="/" style={{ color: '#1a1a1a', textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}>
  ← Back to Search
</a>
  </div>
        <div style={styles.header}>
          <h1 style={styles.title}>Submit an Artist</h1>
          <p style={styles.subtitle}>
            Help us improve our database by submitting artists for verification
          </p>
        </div>

        {submitted && (
          <div style={styles.successMessage}>
            ✅ Thank you! Your submission has been received and will be reviewed.
          </div>
        )}

        <div style={styles.formContainer}>
          <div style={styles.formSection}>
            <label style={styles.label}>Artist/Band Name *</label>
            <input
              type="text"
              name="artistName"
              value={formData.artistName}
              onChange={handleChange}
              placeholder="Enter artist or band name"
              style={styles.input}
              required
            />
          </div>

          <div style={styles.formSection}>
            <label style={styles.label}>What are you submitting? *</label>
            <select
              name="submissionType"
              value={formData.submissionType}
              onChange={handleChange}
              style={styles.select}
            >
              <option value="suspected_ai">🤖 This artist is suspected to be AI-generated</option>
              <option value="verified_human">✅ This is a verified human artist (not in database)</option>
            </select>
            <p style={styles.helper}>
              Choose "Verified Human" if you searched and got no results, but you know this is a real human artist
            </p>
          </div>

          <div style={styles.formSection}>
            <label style={styles.label}>Music Platform Link * (Required)</label>
            <input
              type="url"
              name="musicLink"
              value={formData.musicLink}
              onChange={handleChange}
              placeholder="https://open.spotify.com/artist/... or Deezer, Apple Music, etc."
              style={styles.input}
              required
            />
            <p style={styles.helper}>
              Paste a link from Spotify, Deezer, Apple Music, YouTube Music, Bandcamp, or SoundCloud
            </p>
          </div>

          <div style={styles.formSection}>
            <label style={styles.label}>
              {formData.submissionType === 'suspected_ai' ? 'Evidence/Reasoning' : 'Why is this a human artist?'}
            </label>
            <textarea
              name="evidence"
              value={formData.evidence}
              onChange={handleChange}
              placeholder={
                formData.submissionType === 'suspected_ai'
                  ? "Provide evidence (e.g., 'No social media', 'Releases 20 songs/month', 'Found on Suno AI')"
                  : "Tell us why this is a human artist (e.g., 'Live performances', 'Signed to major label', 'Verified accounts')"
              }
              style={styles.textarea}
              rows="4"
            />
          </div>

          <div style={styles.formSection}>
            <label style={styles.label}>Additional Information (Optional)</label>
            <textarea
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleChange}
              placeholder="Any other details you'd like to share"
              style={styles.textarea}
              rows="3"
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              ...styles.submitButton,
              opacity: submitting ? 0.6 : 1,
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Submitting...' : 'Submit for Review'}
          </button>

          <p style={styles.note}>
            * All submissions are reviewed manually before being added to our database
          </p>
        </div>

        <div style={styles.infoSection}>
          <h3 style={styles.infoTitle}>What happens next?</h3>
          <ul style={styles.infoList}>
            <li style={styles.listItem}>✓ Your submission is added to our review queue</li>
            <li style={styles.listItem}>✓ Our team verifies the information using multiple sources</li>
            <li style={styles.listItem}>✓ Confirmed artists are added to the database within 1-2 weeks</li>
            <li style={styles.listItem}>✓ You can check back to see if the artist appears in search results</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    padding: '40px 20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif'
  },
  content: {
    maxWidth: '700px',
    margin: '0 auto'
  },
  header: {
    textAlign: 'center',
    marginBottom: '40px'
  },
  title: {
    fontSize: '42px',
    color: '#1a1a1a',
    marginBottom: '10px',
    fontWeight: '700',
    letterSpacing: '-0.02em'
  },
  subtitle: {
    fontSize: '18px',
    color: '#666',
    fontWeight: '400'
  },
  successMessage: {
    backgroundColor: '#f0fdf4',
    color: '#166534',
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '30px',
    textAlign: 'center',
    fontWeight: '600',
    border: '1px solid #86efac'
  },
  formContainer: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
  },
  formSection: {
    marginBottom: '24px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  },
  select: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    outline: 'none',
    backgroundColor: 'white',
    cursor: 'pointer',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box'
  },
  helper: {
    fontSize: '13px',
    color: '#666',
    marginTop: '6px',
    fontStyle: 'italic'
  },
  submitButton: {
    width: '100%',
    padding: '16px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: '#1a1a1a',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    marginTop: '10px',
    marginBottom: '20px',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  note: {
    fontSize: '13px',
    color: '#666',
    textAlign: 'center',
    fontStyle: 'italic'
  },
  infoSection: {
    marginTop: '40px',
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '12px',
    border: '1px solid #e0e0e0'
  },
  infoTitle: {
    fontSize: '18px',
    color: '#1a1a1a',
    marginBottom: '16px',
    fontWeight: '700',
    letterSpacing: '-0.01em'
  },
  infoList: {
    listStyle: 'none',
    padding: 0,
    margin: 0
  },
  listItem: {
    padding: '10px 0',
    color: '#666',
    fontSize: '15px',
    paddingLeft: '24px',
    position: 'relative'
  }
};